import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { CriteoSponsoredProduct } from '@jbhi-fi/apis/src/criteo/types';
import {
  getCriteoPlacementId,
  getCriteoProductKey,
  isCriteoSponsoredHit,
  sendCriteoPlacementLoadBeacon,
  sendCriteoPlacementViewBeacon,
} from '@jbhi-fi/utils/src/criteoTracking/criteoBeacons';
import type { ExcludedSponsoredProduct } from '../../utils/algolia/mergeCriteoWithAlgoliaHits';

type UseSponsoredPlacementBeaconsArgs = {
  hits: Array<AlgoliaProductHit>;
  excludedSponsoredProducts?: Array<ExcludedSponsoredProduct>;
  sponsoredContextKey: string;
};

type UseSponsoredPlacementBeaconsReturn = {
  triggerPlacementLoadBeacons: (sponsoredHits: Array<AlgoliaProductHit>) => void;
  handleSponsoredProductView: (hit: AlgoliaProductHit) => void;
};

const getFirstPlacementProductKey = (hits: Array<AlgoliaProductHit>): Map<string, string> => {
  const firstPlacementProductKey = new Map<string, string>();

  hits.filter(isCriteoSponsoredHit).forEach(hit => {
    const placementId = getCriteoPlacementId(hit.criteoProduct);
    const productKey = getCriteoProductKey(hit.criteoProduct);

    if (!placementId || !productKey || firstPlacementProductKey.has(placementId)) {
      return;
    }

    firstPlacementProductKey.set(placementId, productKey);
  });

  return firstPlacementProductKey;
};

const getPlacementProductsToLoad = (
  hits: Array<AlgoliaProductHit>,
  loadedPlacementIds: Set<string>
): Map<string, CriteoSponsoredProduct> => {
  const placementProductsToLoad = new Map<string, CriteoSponsoredProduct>();

  hits.filter(isCriteoSponsoredHit).forEach(hit => {
    const placementId = getCriteoPlacementId(hit.criteoProduct);

    if (!placementId || loadedPlacementIds.has(placementId) || placementProductsToLoad.has(placementId)) {
      return;
    }

    placementProductsToLoad.set(placementId, hit.criteoProduct);
  });

  return placementProductsToLoad;
};

const getExcludedProductsForPlacement = (
  excludedSponsoredProducts: Array<ExcludedSponsoredProduct>,
  placementId: string
): Array<{ productKey: string; statusCode: number }> =>
  excludedSponsoredProducts
    .filter(({ criteoProduct }) => getCriteoPlacementId(criteoProduct) === placementId)
    .map(({ productKey, statusCode }) => ({
      productKey,
      statusCode,
    }));

export const useSponsoredPlacementBeacons = ({
  hits,
  excludedSponsoredProducts = [],
  sponsoredContextKey,
}: UseSponsoredPlacementBeaconsArgs): UseSponsoredPlacementBeaconsReturn => {
  const loadedPlacementIdsRef = useRef<Set<string>>(new Set());
  const viewedPlacementIdsRef = useRef<Set<string>>(new Set());

  const firstPlacementProductKey = useMemo(() => {
    return getFirstPlacementProductKey(hits);
  }, [hits]);

  useEffect(() => {
    loadedPlacementIdsRef.current = new Set();
    viewedPlacementIdsRef.current = new Set();
  }, [sponsoredContextKey]);

  const triggerPlacementLoadBeacons = useCallback(
    (sponsoredHits: Array<AlgoliaProductHit>) => {
      const placementProductsToLoad = getPlacementProductsToLoad(sponsoredHits, loadedPlacementIdsRef.current);

      placementProductsToLoad.forEach((product, placementId) => {
        const excludedProducts = getExcludedProductsForPlacement(excludedSponsoredProducts, placementId);

        sendCriteoPlacementLoadBeacon({
          product,
          excludedProducts,
        });
        loadedPlacementIdsRef.current.add(placementId);
      });
    },
    [excludedSponsoredProducts]
  );

  const handleSponsoredProductView = useCallback(
    (hit: AlgoliaProductHit) => {
      if (!isCriteoSponsoredHit(hit)) {
        return;
      }

      const placementId = getCriteoPlacementId(hit.criteoProduct);
      const productKey = getCriteoProductKey(hit.criteoProduct);

      if (!placementId || !productKey || viewedPlacementIdsRef.current.has(placementId)) {
        return;
      }
      if (firstPlacementProductKey.get(placementId) !== productKey) {
        return;
      }

      sendCriteoPlacementViewBeacon(hit.criteoProduct);
      viewedPlacementIdsRef.current.add(placementId);
    },
    [firstPlacementProductKey]
  );

  return {
    triggerPlacementLoadBeacons,
    handleSponsoredProductView,
  };
};
